import { useState, useEffect, useRef } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { useFgp } from "@/context/FgpContext";
import { StatusBadge } from "@/components/StatusBadge";
import { colorMeta } from "@/lib/colors";
import { toast } from "sonner";
import { uploadFile } from "@/lib/upload";
import { Check, FileText, Download, Phone, Mail, MapPin, Clock, User, Camera, Calendar as CalendarIcon, Plus, X, Tag, Bell, Repeat, Link2, ListChecks, Loader2, Paperclip } from "lucide-react";

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-slate-700">{label}</Label>{children}</div>
);

// Reusable person picker sourced from the People directory (excludes archived).
export const PersonSelect = ({ value, onChange, includeHousehold = true, testid, placeholder = "Select person" }) => {
  const { people } = useFgp();
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {includeHousehold && <SelectItem value="Household">Household</SelectItem>}
        {people.filter((p) => !p.archived).map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

// Reusable chip-style tag input.
const TagInput = ({ tags = [], onChange, testid }) => {
  const [v, setV] = useState("");
  const add = () => { const t = v.trim(); if (!t || tags.includes(t)) return setV(""); onChange([...tags, t]); setV(""); };
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-200 p-2 min-h-[42px]">
      {tags.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 text-xs font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
          {t}<button type="button" onClick={() => onChange(tags.filter((x) => x !== t))} className="hover:text-rose-500"><X className="h-3 w-3" /></button>
        </span>
      ))}
      <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} onBlur={add}
        placeholder="Add tag + Enter" className="text-xs bg-transparent outline-none flex-1 min-w-[110px] py-0.5" data-testid={testid} />
    </div>
  );
};

const PERSON_ROLES = ["Principal", "Spouse", "Ex-spouse", "Child", "Family", "Staff", "Other"];

// Reusable avatar uploader — uploads to object storage, stores a permanent URL.
export const AvatarUpload = ({ value, onChange, fallback }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB");
    setUploading(true);
    try {
      const url = await uploadFile(file);
      onChange(url);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="flex items-center gap-4">
      <div className="relative">
        <Avatar className="h-16 w-16 ring-2 ring-white shadow">
          <AvatarImage src={value} />
          <AvatarFallback className="bg-neutral-100 text-neutral-500">{uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <User className="h-6 w-6" />}</AvatarFallback>
        </Avatar>
        <button type="button" onClick={() => inputRef.current?.click()}
          className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-[#516670] text-white flex items-center justify-center shadow hover:bg-neutral-700"
          data-testid="avatar-upload-btn"><Camera className="h-3 w-3" /></button>
      </div>
      <div>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="avatar-file-input" />
        <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => inputRef.current?.click()}>{uploading ? "Uploading…" : "Upload photo"}</Button>
        {value && <button type="button" onClick={() => onChange("")} className="ml-2 text-xs text-slate-400 hover:text-slate-600" data-testid="avatar-remove-btn">Remove</button>}
        <p className="text-[11px] text-slate-400 mt-1">JPG or PNG, up to 5MB.</p>
      </div>
    </div>
  );
};

// ---------- Add Person ----------
export const AddPersonDialog = ({ open, onOpenChange }) => {
  const { addPerson } = useFgp();
  const blank = { name: "", role: "Family", relation: "", title: "", email: "", phone: "", location: "London", dob: "", tags: [], avatar: "" };
  const [f, setF] = useState(blank);
  const submit = () => {
    if (!f.name.trim()) return toast.error("Name is required");
    const payload = { ...f, email: f.email || "—", phone: f.phone || "—", location: f.location || "—" };
    if (!payload.avatar) delete payload.avatar;
    if (!payload.dob) delete payload.dob;
    addPerson(payload);
    toast.success(`${f.name} added to the directory`);
    onOpenChange(false);
    setF(blank);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-person-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add person</DialogTitle>
          <DialogDescription>Add someone to the principal's network with full details.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2 max-h-[64vh] overflow-y-auto pr-1">
          <AvatarUpload value={f.avatar} onChange={(v) => setF({ ...f, avatar: v })} fallback={f.name?.[0]} />
          <Field label="Full name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="person-name-input" placeholder="e.g. Charlotte Vance" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="person-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PERSON_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Relation"><Input value={f.relation} onChange={(e) => setF({ ...f, relation: e.target.value })} data-testid="person-relation-input" placeholder="e.g. Daughter" /></Field>
          </div>
          <Field label="Title / role"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="person-title-input" placeholder="e.g. Founder & Chairman, Vance Capital" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email"><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="person-email-input" placeholder="name@example.com" /></Field>
            <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="person-phone-input" placeholder="+44 7700 900000" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Location"><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="person-location-input" placeholder="Kensington, London" /></Field>
            <Field label="Date of birth"><Input type="date" value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} data-testid="person-dob-input" /></Field>
          </div>
          <Field label="Tags / notes"><TagInput tags={f.tags} onChange={(tags) => setF({ ...f, tags })} testid="person-tags-input" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-neutral-600 hover:bg-neutral-700" data-testid="person-save-btn">Add person</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Add Contact ----------
export const AddContactDialog = ({ open, onOpenChange }) => {
  const { addContact } = useFgp();
  const [f, setF] = useState({ name: "", category: "Doctor", role: "", phone: "", email: "", address: "", notes: "", avatar: "" });
  const submit = () => {
    if (!f.name.trim()) return toast.error("Name is required");
    const payload = { ...f };
    if (!payload.avatar) delete payload.avatar;
    addContact(payload);
    toast.success(`${f.name} added to contacts`);
    onOpenChange(false);
    setF({ name: "", category: "Doctor", role: "", phone: "", email: "", address: "", notes: "", avatar: "" });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-contact-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add contact</DialogTitle>
          <DialogDescription>Schools, doctors, lawyers and other providers.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2">
          <AvatarUpload value={f.avatar} onChange={(v) => setF({ ...f, avatar: v })} fallback={f.name?.[0]} />
          <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name-input" placeholder="e.g. Dr. James Lin" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="contact-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["School", "Doctor", "Lawyer", "Insurance", "Dentist", "Teacher", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Role / specialty"><Input value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} placeholder="e.g. Pediatrician" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
            <Field label="Email"><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          </div>
          <Field label="Address"><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></Field>
          <Field label="Notes"><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-neutral-600 hover:bg-neutral-700" data-testid="contact-save-btn">Add contact</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Add Task ----------
export const AddTaskDialog = ({ open, onOpenChange, defaultPerson }) => {
  const { addTask, people, taskCategories } = useFgp();
  const [f, setF] = useState({ title: "", description: "", priority: "medium", category: "cat_general", person: defaultPerson || "Household", due: "", recurring: "none" });
  useEffect(() => { if (defaultPerson) setF((p) => ({ ...p, person: defaultPerson })); }, [defaultPerson]);
  const submit = () => {
    if (!f.title.trim()) return toast.error("Task title is required");
    addTask({ ...f, due: f.due || "—", source: "manual" });
    toast.success("Task created");
    onOpenChange(false);
    setF({ title: "", description: "", priority: "medium", category: "cat_general", person: "Household", due: "", recurring: "none" });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-task-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add task</DialogTitle>
          <DialogDescription>Create a task for the team.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2 max-h-[62vh] overflow-y-auto pr-1">
          <Field label="Task"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="task-title-input" placeholder="e.g. Book restaurant for Friday" /></Field>
          <Field label="Description"><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} data-testid="task-desc-input" placeholder="Add any detail, context or links…" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Priority">
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["high", "medium", "low"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Category">
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="task-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {taskCategories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full ${colorMeta(c.color).dot}`} /> {c.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Related to">
              <Select value={f.person} onValueChange={(v) => setF({ ...f, person: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Household">Household</SelectItem>
                  {people.map((p) => <SelectItem key={p.id} value={p.name.split(" ")[0]}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Due date"><Input type="date" value={f.due} onChange={(e) => setF({ ...f, due: e.target.value })} /></Field>
          </div>
          <Field label="Repeat">
            <Select value={f.recurring} onValueChange={(v) => setF({ ...f, recurring: v })}>
              <SelectTrigger data-testid="task-recurring-select"><SelectValue /></SelectTrigger>
              <SelectContent>{[["none", "Does not repeat"], ["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-neutral-600 hover:bg-neutral-700" data-testid="task-save-btn">Create task</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Task Detail (rich, editable) ----------
const TASK_STATUSES = [["todo", "To do"], ["in-progress", "In progress"], ["done", "Done"], ["overdue", "Overdue"]];
const RECURRING = [["none", "Does not repeat"], ["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"]];
const sid = () => `st_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

export const TaskDetailDialog = ({ open, onOpenChange, task }) => {
  const { setTaskStatus, updateTask, taskCategories } = useFgp();
  const [desc, setDesc] = useState("");
  const [newSub, setNewSub] = useState("");
  const [newTag, setNewTag] = useState("");
  useEffect(() => { if (task) setDesc(task.description || ""); }, [task?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!task) return null;

  const cat = taskCategories.find((c) => c.id === task.category);
  const subtasks = task.subtasks || [];
  const tags = task.tags || [];
  const doneSubs = subtasks.filter((s) => s.done).length;
  const change = (v) => { setTaskStatus(task.id, v); toast.success(`Status updated to "${TASK_STATUSES.find(([k]) => k === v)[1]}"`); };

  const addSub = () => { if (!newSub.trim()) return; updateTask(task.id, { subtasks: [...subtasks, { id: sid(), title: newSub.trim(), done: false }] }); setNewSub(""); };
  const toggleSub = (id) => updateTask(task.id, { subtasks: subtasks.map((s) => s.id === id ? { ...s, done: !s.done } : s) });
  const delSub = (id) => updateTask(task.id, { subtasks: subtasks.filter((s) => s.id !== id) });
  const addTag = () => { const v = newTag.trim(); if (!v || tags.includes(v)) return setNewTag(""); updateTask(task.id, { tags: [...tags, v] }); setNewTag(""); };
  const delTag = (t) => updateTask(task.id, { tags: tags.filter((x) => x !== t) });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="task-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{task.title}</DialogTitle>
          <DialogDescription>{task.person} · Due {task.due}{task.recurring && task.recurring !== "none" ? ` · repeats ${task.recurring}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[64vh] overflow-y-auto pr-1">
          <Field label="Description">
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} onBlur={() => updateTask(task.id, { description: desc })} rows={2} data-testid="task-detail-desc" placeholder="Add detail or context…" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Priority">
              <Select value={task.priority} onValueChange={(v) => { updateTask(task.id, { priority: v }); toast.success("Priority updated"); }}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["high", "medium", "low"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Category">
              <Select value={task.category || "cat_general"} onValueChange={(v) => { updateTask(task.id, { category: v }); toast.success("Category updated"); }}>
                <SelectTrigger data-testid="task-detail-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {taskCategories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full ${colorMeta(c.color).dot}`} /> {c.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <Select value={task.status} onValueChange={change}>
                <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_STATUSES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`task-status-option-${v}`}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={<span className="flex items-center gap-1"><Repeat className="h-3 w-3" /> Repeat</span>}>
              <Select value={task.recurring || "none"} onValueChange={(v) => { updateTask(task.id, { recurring: v }); toast.success("Repeat updated"); }}>
                <SelectTrigger data-testid="task-detail-recurring-select"><SelectValue /></SelectTrigger>
                <SelectContent>{RECURRING.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <Field label={<span className="flex items-center gap-1"><Bell className="h-3 w-3" /> Reminder</span>}>
            <Input type="date" value={task.reminder || ""} onChange={(e) => updateTask(task.id, { reminder: e.target.value })} data-testid="task-reminder-input" />
          </Field>

          {/* Subtasks */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-slate-700 flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" /> Checklist</Label>
              {subtasks.length > 0 && <span className="text-xs text-slate-400">{doneSubs}/{subtasks.length} done</span>}
            </div>
            <div className="space-y-1.5">
              {subtasks.map((s) => (
                <div key={s.id} className="flex items-center gap-2.5 rounded-lg border border-slate-200 px-2.5 py-1.5" data-testid={`subtask-${s.id}`}>
                  <Checkbox checked={s.done} onCheckedChange={() => toggleSub(s.id)} data-testid={`subtask-check-${s.id}`} />
                  <span className={`flex-1 text-sm ${s.done ? "line-through text-slate-400" : "text-slate-700"}`}>{s.title}</span>
                  <button onClick={() => delSub(s.id)} className="text-slate-300 hover:text-rose-500" data-testid={`subtask-del-${s.id}`}><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input value={newSub} onChange={(e) => setNewSub(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSub()} placeholder="Add a checklist item…" className="h-9" data-testid="subtask-input" />
              <Button type="button" onClick={addSub} variant="outline" size="sm" className="h-9" data-testid="subtask-add"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>

          {/* Tags */}
          <div>
            <Label className="text-slate-700 flex items-center gap-1 mb-1.5"><Tag className="h-3.5 w-3.5" /> Tags</Label>
            <div className="flex flex-wrap items-center gap-1.5">
              {tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 text-xs font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full" data-testid={`tag-${t}`}>
                  {t}<button onClick={() => delTag(t)} className="hover:text-rose-500"><X className="h-3 w-3" /></button>
                </span>
              ))}
              <input value={newTag} onChange={(e) => setNewTag(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTag()} placeholder="Add tag…"
                className="text-xs bg-transparent outline-none border-b border-dashed border-slate-300 w-24 py-0.5" data-testid="tag-input" />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap pt-1">
            <StatusBadge status={task.status} />
            {cat && <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colorMeta(cat.color).chip}`}>{cat.name}</span>}
            <span className="text-xs text-slate-400">Assignee: {task.assignee || "—"}</span>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="bg-neutral-600 hover:bg-neutral-700" data-testid="task-detail-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Add Renewal ----------
export const AddRenewalDialog = ({ open, onOpenChange }) => {
  const { addRenewal } = useFgp();
  const [f, setF] = useState({ title: "", category: "Insurance", due: "", owner: "Household", amount: "" });
  const submit = () => {
    if (!f.title.trim()) return toast.error("Title is required");
    addRenewal({ ...f, due: f.due || "2026-12-01", amount: f.amount || "—" });
    toast.success("Renewal added");
    onOpenChange(false);
    setF({ title: "", category: "Insurance", due: "", owner: "Household", amount: "" });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-renewal-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add renewal</DialogTitle>
          <DialogDescription>Track anything that lapses.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2">
          <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="renewal-title-input" placeholder="e.g. Travel insurance" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Insurance", "Travel", "Immigration", "Health", "Education", "Other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Due date"><Input type="date" value={f.due} onChange={(e) => setF({ ...f, due: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Owner"><PersonSelect value={f.owner} onChange={(v) => setF({ ...f, owner: v })} testid="renewal-owner-select" /></Field>
            <Field label="Amount"><Input value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="£0/yr" /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-neutral-600 hover:bg-neutral-700" data-testid="renewal-save-btn">Add renewal</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Add Event ----------
export const AddEventDialog = ({ open, onOpenChange, defaultDate }) => {
  const { addEvent } = useFgp();
  const [f, setF] = useState({ title: "", date: defaultDate || "", time: "09:00", category: "Household", location: "", person: "Household", attendees: "", link: "", description: "" });
  useEffect(() => { if (defaultDate) setF((p) => ({ ...p, date: defaultDate })); }, [defaultDate]);
  const submit = () => {
    if (!f.title.trim()) return toast.error("Title is required");
    addEvent({ ...f, date: f.date || "2026-07-15" });
    toast.success("Event added to calendar");
    onOpenChange(false);
    setF({ title: "", date: "", time: "09:00", category: "Household", location: "", person: "Household", attendees: "", link: "", description: "" });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-event-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add event</DialogTitle>
          <DialogDescription>Add to the shared calendar.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2 max-h-[62vh] overflow-y-auto pr-1">
          <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="event-title-input" placeholder="e.g. Board meeting" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date"><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
            <Field label="Time"><Input value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["School", "Travel", "Health", "Family", "Social", "Household"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Location"><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Person"><PersonSelect value={f.person} onChange={(v) => setF({ ...f, person: v })} testid="event-person-select" /></Field>
            <Field label="Attendees"><Input value={f.attendees} onChange={(e) => setF({ ...f, attendees: e.target.value })} data-testid="event-attendees-input" placeholder="e.g. Jonathan, Eleanor" /></Field>
          </div>
          <Field label="Meeting link"><Input value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} data-testid="event-link-input" placeholder="https://…" /></Field>
          <Field label="Notes"><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} data-testid="event-desc-input" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-neutral-600 hover:bg-neutral-700" data-testid="event-save-btn">Add event</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Upload Document ----------
export const UploadDocDialog = ({ open, onOpenChange }) => {
  const { addDocument, people } = useFgp();
  const blank = { name: "", type: "Passport", person: "ws", number: "", uploaded: new Date().toISOString().slice(0, 10), expiry: "", status: "valid", notes: "", fileUrl: "", fileName: "" };
  const [f, setF] = useState(blank);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) return toast.error("File must be under 15MB");
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setF((p) => ({ ...p, fileUrl: url, fileName: file.name, name: p.name || file.name.replace(/\.[^.]+$/, "") }));
      toast.success("File uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  const submit = () => {
    if (!f.name.trim()) return toast.error("Document name is required");
    addDocument({ ...f, expiry: f.expiry || "—" });
    toast.success("Document saved");
    onOpenChange(false);
    setF(blank);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upload-doc-dialog">
        <DialogHeader><DialogTitle className="font-heading">Upload document</DialogTitle>
          <DialogDescription>Assign it to a person, categorise it and track its expiry.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2 max-h-[64vh] overflow-y-auto pr-1">
          <input ref={fileRef} type="file" accept="image/*,application/pdf,.doc,.docx" className="hidden" onChange={onFile} data-testid="doc-file-input" />
          {f.fileUrl ? (
            <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="doc-file-attached">
              <span className="flex items-center gap-2 text-sm text-slate-700 min-w-0"><Paperclip className="h-4 w-4 flex-shrink-0 text-neutral-500" /><span className="truncate">{f.fileName}</span></span>
              <Button type="button" variant="outline" size="sm" onClick={() => setF({ ...f, fileUrl: "", fileName: "" })} data-testid="doc-file-remove">Remove</Button>
            </div>
          ) : (
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="doc-upload-btn"
              className="w-full rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-400 hover:border-slate-300 transition-colors disabled:opacity-60">
              {uploading ? <><Loader2 className="h-6 w-6 mx-auto mb-2 animate-spin" /> Uploading…</> : <><FileText className="h-6 w-6 mx-auto mb-2" /> Click to upload a file (PDF, image, up to 15MB)</>}
            </button>
          )}
          <Field label="Document name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="doc-name-input" placeholder="e.g. Passport — Charlotte Vance" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category / type">
              <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Passport", "Visa", "Insurance", "Licence", "Legal", "Vehicle", "Medical", "Financial", "Other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Assigned to">
              <Select value={f.person} onValueChange={(v) => setF({ ...f, person: v })}>
                <SelectTrigger data-testid="doc-owner-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ws">Household</SelectItem>
                  {people.filter((p) => !p.archived).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Reference / number"><Input value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} data-testid="doc-number-input" placeholder="e.g. 5334xxxxx" /></Field>
            <Field label="Status">
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="doc-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{[["valid", "Valid"], ["expiring", "Expiring"], ["overdue", "Overdue"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Issued / uploaded"><Input type="date" value={f.uploaded} onChange={(e) => setF({ ...f, uploaded: e.target.value })} data-testid="doc-uploaded-input" /></Field>
            <Field label="Expiry date"><Input type="date" value={f.expiry} onChange={(e) => setF({ ...f, expiry: e.target.value })} data-testid="doc-expiry-input" /></Field>
          </div>
          <Field label="Notes"><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} data-testid="doc-notes-input" placeholder="Any extra detail…" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-neutral-600 hover:bg-neutral-700" data-testid="doc-save-btn">Upload</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Edit Profile ----------
export const EditProfileDialog = ({ open, onOpenChange }) => {
  const { currentUser, updateProfile } = useFgp();
  const [f, setF] = useState(currentUser);
  useEffect(() => { if (open) setF(currentUser); }, [open, currentUser]);
  const submit = () => { updateProfile(f); toast.success("Profile updated"); onOpenChange(false); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-profile-dialog">
        <DialogHeader><DialogTitle className="font-heading">Edit profile</DialogTitle></DialogHeader>
        <div className="py-2">
          <AvatarUpload value={f.avatar} onChange={(v) => setF({ ...f, avatar: v })} fallback={f.name?.[0]} />
        </div>
        <div className="space-y-4">
          <Field label="Full name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="profile-name-input" /></Field>
          <Field label="Role"><Input value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} /></Field>
          <Field label="Email"><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label="Phone"><Input value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-neutral-600 hover:bg-neutral-700" data-testid="profile-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Link Contacts to a Person ----------
export const LinkContactsDialog = ({ open, onOpenChange, personId }) => {
  const { contacts, toggleContactLink, getPerson } = useFgp();
  const person = getPerson(personId);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="link-contacts-dialog">
        <DialogHeader><DialogTitle className="font-heading">Link contacts</DialogTitle>
          <DialogDescription>Connect contacts to {person?.name}.</DialogDescription></DialogHeader>
        <div className="space-y-2 py-2 max-h-80 overflow-y-auto">
          {contacts.map((c) => {
            const linked = (c.linkedPeople || []).includes(personId);
            return (
              <button key={c.id} onClick={() => toggleContactLink(c.id, personId)} data-testid={`link-contact-${c.id}`}
                className={`w-full flex items-center justify-between rounded-lg border px-3 py-2.5 text-left transition-colors ${linked ? "border-neutral-300 bg-neutral-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <div><p className="text-sm font-medium text-slate-800">{c.name}</p><p className="text-xs text-slate-400">{c.category} · {c.role}</p></div>
                {linked && <span className="h-6 w-6 rounded-full bg-neutral-600 text-white flex items-center justify-center"><Check className="h-3.5 w-3.5" /></span>}
              </button>
            );
          })}
        </div>
        <DialogFooter><Button onClick={() => onOpenChange(false)} className="bg-neutral-600 hover:bg-neutral-700">Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Event Detail ----------
export const EventDetailDialog = ({ open, onOpenChange, event }) => {
  const { updateEvent } = useFgp();
  const [notes, setNotes] = useState("");
  useEffect(() => { if (event) setNotes(event.description || ""); }, [event?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!event) return null;
  const dateLabel = new Date(event.date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="event-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-neutral-100 text-neutral-700 flex items-center justify-center"><CalendarIcon className="h-5 w-5" /></div>
            <div><DialogTitle className="font-heading">{event.title}</DialogTitle>
              <DialogDescription>{event.category} event</DialogDescription></div>
          </div>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2 max-h-[60vh] overflow-y-auto pr-1">
          <div className="rounded-lg border border-slate-200 p-3 col-span-2"><p className="text-xs text-slate-400 flex items-center gap-1"><CalendarIcon className="h-3 w-3" /> Date</p><p className="text-sm font-medium text-slate-800 mt-0.5">{dateLabel}</p></div>
          <div className="rounded-lg border border-slate-200 p-3"><p className="text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" /> Time</p><p className="text-sm font-medium text-slate-800 mt-0.5">{event.time}</p></div>
          <div className="rounded-lg border border-slate-200 p-3"><p className="text-xs text-slate-400 flex items-center gap-1"><User className="h-3 w-3" /> Person</p><p className="text-sm font-medium text-slate-800 mt-0.5">{event.person || "—"}</p></div>
          <div className="rounded-lg border border-slate-200 p-3 col-span-2"><p className="text-xs text-slate-400 flex items-center gap-1"><MapPin className="h-3 w-3" /> Location</p><p className="text-sm font-medium text-slate-800 mt-0.5">{event.location || "—"}</p></div>
          {event.attendees && <div className="rounded-lg border border-slate-200 p-3 col-span-2"><p className="text-xs text-slate-400 flex items-center gap-1"><User className="h-3 w-3" /> Attendees</p><p className="text-sm font-medium text-slate-800 mt-0.5">{event.attendees}</p></div>}
          {event.link && <div className="rounded-lg border border-slate-200 p-3 col-span-2"><p className="text-xs text-slate-400 flex items-center gap-1"><Link2 className="h-3 w-3" /> Link</p><a href={event.link} target="_blank" rel="noreferrer" className="text-sm font-medium text-neutral-700 hover:underline mt-0.5 break-all" data-testid="event-link">{event.link}</a></div>}
          <div className="col-span-2">
            <p className="text-xs text-slate-400 mb-1">Notes</p>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => updateEvent(event.id, { description: notes })} rows={2} data-testid="event-detail-notes" placeholder="Add prep notes…" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="bg-neutral-600 hover:bg-neutral-700">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
export const DocumentViewDialog = ({ open, onOpenChange, doc, ownerName }) => {
  if (!doc) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="doc-view-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-neutral-50 text-neutral-600 flex items-center justify-center"><FileText className="h-5 w-5" /></div>
            <div><DialogTitle className="font-heading">{doc.name}</DialogTitle>
              <DialogDescription>{doc.type}</DialogDescription></div>
          </div>
        </DialogHeader>
        <div className="rounded-xl border border-slate-200 bg-slate-50 h-44 flex items-center justify-center text-slate-300 my-2">
          <FileText className="h-12 w-12" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[["Owner", ownerName], ["Type", doc.type], ["Reference", doc.number || "—"], ["Expiry", doc.expiry], ["Uploaded", doc.uploaded]].map(([l, v]) => (
            <div key={l} className="rounded-lg border border-slate-200 p-3"><p className="text-xs text-slate-400">{l}</p><p className="text-sm font-medium text-slate-800 mt-0.5">{v}</p></div>
          ))}
          {doc.notes && <div className="rounded-lg border border-slate-200 p-3 col-span-2"><p className="text-xs text-slate-400">Notes</p><p className="text-sm text-slate-700 mt-0.5">{doc.notes}</p></div>}
        </div>
        <div className="mt-2"><StatusBadge status={doc.status} /></div>
        <DialogFooter>
          {doc.fileUrl
            ? <a href={doc.fileUrl} target="_blank" rel="noreferrer" data-testid="doc-open-file"><Button variant="outline" className="gap-1.5"><Download className="h-4 w-4" /> Open file</Button></a>
            : <Button variant="outline" disabled className="gap-1.5"><Download className="h-4 w-4" /> No file attached</Button>}
          <Button onClick={() => onOpenChange(false)} className="bg-neutral-600 hover:bg-neutral-700">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Edit Person ----------
export const EditPersonDialog = ({ open, onOpenChange, person }) => {
  const { updatePerson } = useFgp();
  const [f, setF] = useState(person || {});
  useEffect(() => { if (open && person) setF(person); }, [open, person]);
  if (!person) return null;
  const submit = () => {
    if (!f.name?.trim()) return toast.error("Name is required");
    updatePerson(person.id, f);
    toast.success("Person updated");
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-person-dialog">
        <DialogHeader><DialogTitle className="font-heading">Edit person</DialogTitle>
          <DialogDescription>Update this person's details.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2 max-h-[62vh] overflow-y-auto pr-1">
          <AvatarUpload value={f.avatar} onChange={(v) => setF({ ...f, avatar: v })} fallback={f.name?.[0]} />
          <Field label="Full name"><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="edit-person-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={f.role || "Family"} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="edit-person-role"><SelectValue /></SelectTrigger>
                <SelectContent>{["Principal", "Spouse", "Ex-spouse", "Child", "Family", "Staff", "Other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Relation"><Input value={f.relation || ""} onChange={(e) => setF({ ...f, relation: e.target.value })} placeholder="e.g. Daughter" /></Field>
          </div>
          <Field label="Title / role"><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="edit-person-title" placeholder="e.g. Founder & Chairman, Vance Capital" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email"><Input value={f.email === "—" ? "" : (f.email || "")} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="edit-person-email" /></Field>
            <Field label="Phone"><Input value={f.phone === "—" ? "" : (f.phone || "")} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="edit-person-phone" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Location"><Input value={f.location === "—" ? "" : (f.location || "")} onChange={(e) => setF({ ...f, location: e.target.value })} /></Field>
            <Field label="Date of birth"><Input type="date" value={f.dob || ""} onChange={(e) => setF({ ...f, dob: e.target.value })} data-testid="edit-person-dob" /></Field>
          </div>
          <Field label="Tags / notes"><TagInput tags={f.tags || []} onChange={(tags) => setF({ ...f, tags })} testid="edit-person-tags" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-neutral-600 hover:bg-neutral-700" data-testid="edit-person-save">Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Note editor (create text note + edit any note) ----------
export const NoteDialog = ({ open, onOpenChange, note }) => {
  const { addNote, updateNote } = useFgp();
  const editing = !!note;
  const blank = { title: "", transcript: "", type: "text" };
  const [f, setF] = useState(note || blank);
  useEffect(() => { if (open) setF(note || blank); }, [open, note]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = () => {
    if (!f.title.trim()) return toast.error("Title is required");
    if (editing) { updateNote(note.id, { title: f.title, transcript: f.transcript }); toast.success("Note updated"); }
    else { addNote({ title: f.title, transcript: f.transcript, type: "text", duration: "—", suggestedTasks: [] }); toast.success("Note saved"); }
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="note-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit note" : "New note"}</DialogTitle>
          <DialogDescription>{editing ? "Update this note." : "Write a quick note."}</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2">
          <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="note-title-input" placeholder="e.g. Call with school" /></Field>
          <Field label="Note"><Textarea value={f.transcript} onChange={(e) => setF({ ...f, transcript: e.target.value })} rows={6} data-testid="note-body-input" placeholder="Write your note…" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-neutral-600 hover:bg-neutral-700" data-testid="note-save-btn">{editing ? "Save" : "Save note"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Edit Document ----------
export const EditDocDialog = ({ open, onOpenChange, doc }) => {
  const { updateDocument, people } = useFgp();
  const [f, setF] = useState(doc || {});
  useEffect(() => { if (open && doc) setF(doc); }, [open, doc]);
  if (!doc) return null;
  const submit = () => {
    if (!f.name?.trim()) return toast.error("Document name is required");
    updateDocument(doc.id, f);
    toast.success("Document updated");
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-doc-dialog">
        <DialogHeader><DialogTitle className="font-heading">Edit document</DialogTitle>
          <DialogDescription>Update metadata (demo — no real file).</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2 max-h-[64vh] overflow-y-auto pr-1">
          <Field label="Document name"><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="edit-doc-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category / type">
              <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Passport", "Visa", "Insurance", "Licence", "Legal", "Vehicle", "Medical", "Financial", "Other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Assigned to">
              <Select value={f.person} onValueChange={(v) => setF({ ...f, person: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ws">Household</SelectItem>
                  {people.filter((p) => !p.archived).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Reference / number"><Input value={f.number || ""} onChange={(e) => setF({ ...f, number: e.target.value })} data-testid="edit-doc-number" /></Field>
            <Field label="Status">
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="edit-doc-status"><SelectValue /></SelectTrigger>
                <SelectContent>{[["valid", "Valid"], ["expiring", "Expiring"], ["overdue", "Overdue"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Issued / uploaded"><Input type="date" value={f.uploaded === "—" ? "" : (f.uploaded || "")} onChange={(e) => setF({ ...f, uploaded: e.target.value })} /></Field>
            <Field label="Expiry date"><Input type="date" value={f.expiry === "—" ? "" : (f.expiry || "")} onChange={(e) => setF({ ...f, expiry: e.target.value || "—" })} /></Field>
          </div>
          <Field label="Notes"><Textarea value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} data-testid="edit-doc-notes" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-neutral-600 hover:bg-neutral-700" data-testid="edit-doc-save">Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
